import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { observer } from 'mobx-react-lite';
import { useEffect, type ReactElement } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import { useAuthStore } from '../stores';
import { Routes } from './routes';

/**
 * AuthGate — observer that protects authed routes. Reads the MobX
 * `authStore.isAuthenticated` + `.hydrated`:
 *
 *   - not hydrated → spinner (one-tick bootstrap before the redirect /
 *     mount decision is final)
 *   - hydrated + not authed → `<Navigate to={Routes.Auth.Login} replace />`
 *   - hydrated + authed → `<Outlet />` for the AppShell tree
 *
 * The `replace` flag means the unauthed redirect doesn't accumulate in
 * the history stack — a back button after login goes to wherever the
 * operator came from, not the login screen again.
 */
export const AuthGate = observer((): ReactElement => {
  const auth = useAuthStore();

  useEffect(() => {
    if (!auth.hydrated) {
      void auth.hydrate();
    }
  }, [auth]);

  if (!auth.hydrated) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!auth.isAuthenticated) {
    return <Navigate to={Routes.Auth.Login} replace />;
  }

  return <Outlet />;
});
